import { useState, useEffect, useRef, useContext } from 'react'
import { AdminContext } from 'context/AdminContext'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { MagnifyingGlassIcon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/20/solid'

export default function UserDirectoryGroupsDropdown() {
    const { UserDirectorySubContext } = useContext(AdminContext)

    const [groups, setGroups] = useState([])
    const [groupsLoading, setGroupsLoading] = useState(true)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const dropdownRef = useRef(null)

    const FetchGroups = async () => {
        try {
            setGroupsLoading(true)
            const result = await WhichWeightAPI('REPORTING', 'GET', '/kinesis_user_groups_directory')

            if (result === 'failure' || result?.data?.status === 'failure') {
                setGroups([])
                return
            }

            if (result.data.data) {
                setGroups(result.data.data)
            }
        } catch (error) {
            console.error('Error fetching Kinesis user groups:', error)
        } finally {
            setGroupsLoading(false)
        }
    }

    const HandleClickOutsideDropdown = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
            setIsDropdownOpen(false)
            setSearchTerm('')
        }
    }

    const GetSelectedGroup = () => {
        const fid = UserDirectorySubContext.userDirectoryFilters.kinesisUserGroupIdFilter
        if (!fid) return { name: 'All Groups', imageUrl: null }
        if (fid === 'none') return { name: 'No group', imageUrl: null }
        const group = groups.find((g) => g.id.toString() === fid)
        return group || { name: 'All Groups', imageUrl: null }
    }

    useEffect(() => { FetchGroups() }, [])

    useEffect(() => {
        document.addEventListener('mousedown', HandleClickOutsideDropdown)
        return () => {
            document.removeEventListener('mousedown', HandleClickOutsideDropdown)
        }
    }, [])

    const filteredGroups = groups.filter((g) => g.name.toLowerCase().includes(searchTerm.toLowerCase()))
    const selectedGroup = GetSelectedGroup()
    const hasSelection = Boolean(UserDirectorySubContext.userDirectoryFilters.kinesisUserGroupIdFilter)

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                type="button"
                className={`w-full flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-left shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 hover:border-gray-400 ${hasSelection ? 'pr-14' : 'pr-9'}`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
                <div className="flex items-center gap-x-3 min-w-0">
                    {selectedGroup.imageUrl ? (
                        <img
                            src={selectedGroup.imageUrl}
                            alt=""
                            className="w-5 h-5 rounded-full border border-gray-200 flex-shrink-0"
                            onError={(e) => {
                                e.target.style.display = 'none'
                            }}
                        />
                    ) : (
                        <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs text-gray-500">G</span>
                        </div>
                    )}

                    <span className="truncate">{selectedGroup.name}</span>
                </div>

            </button>

            {hasSelection && (
                <button
                    type="button"
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100 transition-colors focus:outline-none z-10"
                    onClick={() => {
                        UserDirectorySubContext.HandleUserDirectoryFiltersChange('kinesisUserGroupIdFilter', '')
                    }}
                    title="Clear selection"
                >
                    <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
            )}

            <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-1 py-1 focus:outline-none z-10"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                title="Toggle group list"
            >
                <ChevronDownIcon
                    className={`
                        h-5 w-5 text-gray-400 transition-transform
                        ${isDropdownOpen ? 'rotate-180' : ''}
                    `}
                />
            </button>

            {isDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-300 bg-white shadow-lg">
                    <div className="max-h-[400px] overflow-auto">
                        <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                            <div className="relative">
                                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    className="w-full rounded-md border border-gray-300 py-2 pl-10 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder="Search groups..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    autoFocus
                                />
                            </div>
                        </div>

                        <div
                            className="flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                                UserDirectorySubContext.HandleUserDirectoryFiltersChange('kinesisUserGroupIdFilter', '')
                                setIsDropdownOpen(false)
                                setSearchTerm('')
                            }}
                        >
                            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-xs text-gray-500">G</span>
                            </div>

                            <span>All Groups</span>
                        </div>

                        <div
                            className="flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => {
                                UserDirectorySubContext.HandleUserDirectoryFiltersChange('kinesisUserGroupIdFilter', 'none')
                                setIsDropdownOpen(false)
                                setSearchTerm('')
                            }}
                        >
                            <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                                <span className="text-xs text-gray-500">—</span>
                            </div>

                            <span>No group</span>
                        </div>

                        {groupsLoading && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                Loading groups...
                            </div>
                        )}

                        {!groupsLoading && filteredGroups.length === 0 && searchTerm && (
                            <div className="px-3 py-2 text-sm text-gray-500">
                                No groups found
                            </div>
                        )}

                        {!groupsLoading && filteredGroups.map((group) => (
                            <div
                                key={group.id}
                                className="flex items-center gap-x-3 px-3 py-2 cursor-pointer hover:bg-gray-100"
                                onClick={() => {
                                    UserDirectorySubContext.HandleUserDirectoryFiltersChange(
                                        'kinesisUserGroupIdFilter',
                                        group.id.toString()
                                    )
                                    setIsDropdownOpen(false)
                                    setSearchTerm('')
                                }}
                            >
                                {group.imageUrl ? (
                                    <img
                                        src={group.imageUrl}
                                        alt=""
                                        className="w-5 h-5 rounded-full border border-gray-200"
                                        onError={(e) => {
                                            e.target.style.display = 'none'
                                        }}
                                    />
                                ) : (
                                    <div className="w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center">
                                        <span className="text-xs text-gray-500">G</span>
                                    </div>
                                )}

                                <span className="truncate">{group.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}
